import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { CardScalarControl } from "@/components/card/shared/CardScalarControl";
import { formatReadonlyScalar } from "@/components/card/shared/formatScalarDisplay";
import { useKlsOptions } from "@/hooks/useKlsOptions";
import { cn } from "@/lib/utils";
import { GoFieldProvenanceControl } from "@/components/go/GoFieldProvenanceControl";
import type { GoEntityCardFieldValue, GoFieldProvenancePatch } from "@/types/go-entity-card";

export type GoCardFieldCommitResult = { ok: boolean };

type Props = {
  field: GoEntityCardFieldValue;
  editing?: boolean;
  highlighted?: boolean;
  saving?: boolean;
  showFieldKey?: boolean;
  onCommit?: (fieldKey: string, value: unknown) => Promise<GoCardFieldCommitResult | void>;
  onMarkNa?: (fieldKey: string) => Promise<GoCardFieldCommitResult | void>;
  onClearNa?: (fieldKey: string) => Promise<GoCardFieldCommitResult | void>;
  onSaveProvenance?: (
    fieldKey: string,
    patch: GoFieldProvenancePatch,
  ) => Promise<GoCardFieldCommitResult | void>;
  onFocusField?: (fieldKey: string) => void;
  className?: string;
};

function normalizeFieldType(raw: string): string {
  const t = (raw || "string").toLowerCase().trim();
  if (t === "bool") return "boolean";
  if (t === "int" || t === "float" || t === "numeric") return "number";
  return t;
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

/**
 * Контрол значения поля карточки ГО: виджет по field_type / N/A / saving / highlight.
 */
export function GoCardFieldControl({
  field,
  editing = false,
  highlighted = false,
  saving = false,
  showFieldKey = false,
  onCommit,
  onMarkNa,
  onClearNa,
  onSaveProvenance,
  onFocusField,
  className,
}: Props) {
  const isNa = !!field.is_na;
  const locked = field.is_readonly || !!field.is_masked || !editing || isNa;
  const fieldType = normalizeFieldType(field.field_type);
  const isClassifier = fieldType === "classifier" || fieldType === "classifier_tree";
  const [draft, setDraft] = useState<unknown>(field.value);
  const [localError, setLocalError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const baselineRef = useRef<unknown>(field.value);

  const klsQuery = useKlsOptions(isClassifier && editing && !isNa ? field.qual_id : null);

  const comboboxOptions: ComboboxOption[] = useMemo(
    () =>
      (klsQuery.data ?? []).map((o) => ({
        value: String(o.value),
        label: o.label,
        shortLabel: o.shortLabel,
        fullLabel: o.fullLabel,
        indentLevel: o.indentLevel,
      })),
    [klsQuery.data],
  );

  useEffect(() => {
    setDraft(field.value);
    baselineRef.current = field.value;
    setLocalError(null);
  }, [field.field_key, field.value, field.is_masked, field.is_na, field.field_type]);

  useEffect(() => {
    if (!highlighted || !rootRef.current) return;
    rootRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [highlighted, field.field_key]);

  const commitValue = async (next: unknown) => {
    if (locked || !onCommit || saving || isNa) return;
    if (valuesEqual(next, baselineRef.current)) return;
    setLocalError(null);
    try {
      const result = await onCommit(field.field_key, next);
      if (result && result.ok === false) {
        setLocalError("Не сохранено");
        return;
      }
      baselineRef.current = next;
      setDraft(next);
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : "Ошибка сохранения");
    }
  };

  const onDraftChange = (next: unknown) => {
    setDraft(next);
    if (fieldType === "boolean") {
      void commitValue(next);
    }
  };

  const readonlyText = field.is_masked
    ? "••••••••"
    : fieldType === "boolean"
      ? field.value === true
        ? "Да"
        : field.value === false
          ? "Нет"
          : "—"
      : formatReadonlyScalar(field.value);

  const controlId = `go-field-${field.field_key}`;

  return (
    <div
      ref={rootRef}
      data-field-key={field.field_key}
      className={cn(
        "space-y-1 rounded-md transition-shadow",
        highlighted && "ring-2 ring-primary/60 ring-offset-2 ring-offset-background",
        className,
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Label htmlFor={controlId} className="text-foreground leading-snug">
          {field.label}
          {field.is_masked ? (
            <span className="ml-2 text-xs font-normal text-amber-700">скрыто</span>
          ) : null}
          {isNa ? (
            <span className="ml-2 text-xs font-normal text-muted-foreground">⊘ N/A</span>
          ) : null}
          {field.is_readonly ? (
            <span className="ml-2 text-xs font-normal text-muted-foreground">только чтение</span>
          ) : null}
          {saving ? (
            <span className="ml-2 text-xs font-normal text-muted-foreground">сохранение…</span>
          ) : null}
        </Label>
        <div className="flex items-center gap-2">
          {showFieldKey ? (
            <code className="text-[10px] text-muted-foreground">{field.field_key}</code>
          ) : null}
          {!field.is_masked ? (
            <GoFieldProvenanceControl
              field={field}
              editing={editing && !field.is_readonly}
              saving={saving}
              onSaveProvenance={onSaveProvenance}
            />
          ) : null}
          {editing && !field.is_readonly && !field.is_masked ? (
            isNa ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={saving || !onClearNa}
                onClick={() => void onClearNa?.(field.field_key)}
              >
                Снять N/A
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                disabled={saving || !onMarkNa}
                onClick={() => void onMarkNa?.(field.field_key)}
              >
                N/A
              </Button>
            )
          ) : null}
        </div>
      </div>
      {isNa && !editing ? (
        <div className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
          ⊘ Неприменимо
        </div>
      ) : isNa && editing ? (
        <div className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
          ⊘ N/A — снимите пометку, чтобы ввести значение
        </div>
      ) : !editing || locked ? (
        <div
          className={cn(
            "rounded-md border border-border bg-muted/20 px-3 py-2 text-sm text-foreground break-words",
            locked && editing && "opacity-80",
          )}
        >
          {readonlyText || "—"}
        </div>
      ) : isClassifier && field.qual_id ? (
        <div onFocus={() => onFocusField?.(field.field_key)}>
          <Combobox
            options={comboboxOptions}
            value={draft == null ? undefined : String(draft)}
            onValueChange={(v) => {
              const next = v == null || v === "" ? null : Number(v);
              setDraft(next);
              void commitValue(Number.isFinite(next as number) ? next : null);
            }}
            disabled={saving || klsQuery.isLoading}
            placeholder={klsQuery.isLoading ? "Загрузка…" : "Выберите значение"}
            allowClear
          />
        </div>
      ) : (
        <div
          onFocus={() => onFocusField?.(field.field_key)}
          onBlur={() => {
            if (fieldType !== "boolean") void commitValue(draft);
          }}
        >
          <CardScalarControl
            id={controlId}
            fieldType={fieldType}
            value={draft}
            onChange={onDraftChange}
            disabled={saving}
            booleanVariant="switch"
            validationRules={field.validation_rules ?? undefined}
          />
        </div>
      )}
      {localError ? <p className="text-[11px] text-destructive">{localError}</p> : null}
    </div>
  );
}
